import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessToken } from '../../awsAuth';
import './HomeChatsPanel.scss';

const DEFAULT_AVATAR =
  'https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png';

const API_BASE = process.env.REACT_APP_API_BASE || '';

// Reuses the exact same conversations endpoint as the real Messages page
// (src/components/Messages/Messages.js) — no parallel chat system, no mock
// data. Only a compact read-only preview list; opening a thread hands off
// to the existing /messages/:threadId route and chat flow.
function formatRelativeTime(ts) {
  if (!ts) return '';
  const diffMs = Date.now() - Number(ts);
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'зараз';
  if (min < 60) return `${min} хв`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} год`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} д`;
  return new Date(Number(ts)).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' });
}

const HomeChatsPanel = () => {
  const { userId, isLoggedIn } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoggedIn || !userId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const token = await getAccessToken();
        const res = await axios.get(`${API_BASE}/api/messages/conversations`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const raw = Array.isArray(res.data) ? res.data : [];
        const convs = raw
          .map((it) => ({
            threadId: it.threadId,
            peerId: it.peerId,
            lastText: it.lastText || '',
            lastAt: Number(it.lastAt || 0),
            unread: Number(it.unread || 0),
            peerUsername: it.peerUsername || 'Користувач',
            peerAvatar: it.peerAvatar || null,
          }))
          .sort((a, b) => b.lastAt - a.lastAt)
          .slice(0, 5);

        if (!cancelled) setConversations(convs);
      } catch (e) {
        if (!cancelled) setConversations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, userId]);

  if (!isLoggedIn) return null;

  return (
    <aside className="home-chats" aria-label="Повідомлення">
      <div className="home-chats__head">
        <span className="home-chats__title">Повідомлення</span>
        <Link to="/messages" className="home-chats__all">Усі</Link>
      </div>

      {loading ? (
        <div className="home-chats__empty">Завантаження…</div>
      ) : conversations.length === 0 ? (
        <div className="home-chats__empty">Поки немає повідомлень</div>
      ) : (
        <div className="home-chats__list">
          {conversations.map((c) => (
            <Link key={c.threadId} to={`/messages/${c.threadId}`} className="home-chats__row">
              <img
                src={c.peerAvatar || DEFAULT_AVATAR}
                alt=""
                className="home-chats__avatar"
                loading="lazy"
              />

              <div className="home-chats__meta">
                <div className="home-chats__row-top">
                  <span className="home-chats__name">{c.peerUsername}</span>
                  {c.lastAt > 0 && (
                    <span className="home-chats__time">{formatRelativeTime(c.lastAt)}</span>
                  )}
                </div>

                <div className="home-chats__row-bottom">
                  <span className="home-chats__preview">{c.lastText || '…'}</span>
                  {c.unread > 0 && (
                    <span className="home-chats__unread">{c.unread > 9 ? '9+' : c.unread}</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </aside>
  );
};

export default HomeChatsPanel;
